import type { SetState } from '../store';
import type { UpdatedJackpotTier } from '@/types/zones/slices/site-wide-jackpot-slice';

export type MultiplierValue = 0 | 1 | 2;

export type SiteWideJackpotSliceProps = {
    siteWideJackpot: {
        multiplier: MultiplierValue;
        setMultiplier: (value: MultiplierValue) => void;

        updatedTiers: UpdatedJackpotTier[];
        error: Error | null;
        setUpdatedTiers: (tiers: UpdatedJackpotTier[] | ((prev: UpdatedJackpotTier[]) => UpdatedJackpotTier[])) => void;

        loading: boolean;
        setLoading: (value: boolean) => void;

        activeIndex: number;
        hasSwjInitialized: boolean | undefined;
        setActiveIndex: (index: number | ((prev: number) => number)) => void;

        // MDS-related actions
        setHasSwjInitialized: (value: boolean | undefined) => void;
        setOptInStatusError: (multiplier: MultiplierValue) => void;

        // DYB tutorial preferences
        showDYBTutorial: boolean | undefined;
        showDYBTutorialCounter: number | undefined;
        showDYBTutorialV2: boolean | undefined;
        showDYBTutorialV2Counter: number | undefined;
        showDYBTracker: boolean | undefined;
        setDYBUserPreferences: (preferences: {
            showDYBTutorial?: boolean;
            showDYBTutorialCounter?: number;
            showDYBTutorialV2?: boolean;
            showDYBTutorialV2Counter?: number;
            showDYBTracker?: boolean;
        }) => void;
    };
};

export const siteWideJackpotSlice = ({ set }: SetState<SiteWideJackpotSliceProps>): SiteWideJackpotSliceProps => ({
    siteWideJackpot: {
        multiplier: 0,
        setMultiplier: (value: MultiplierValue) => {
            set(
                (state) => ({
                    siteWideJackpot: {
                        ...state.siteWideJackpot,
                        multiplier: value
                    }
                }),
                undefined,
                'siteWideJackpot/setMultiplier'
            );
        },

        loading: false,
        setLoading: (value: boolean) => {
            set(
                (state) => ({
                    siteWideJackpot: {
                        ...state.siteWideJackpot,
                        loading: value
                    }
                }),
                undefined,
                'siteWideJackpot/setLoading'
            );
        },

        updatedTiers: [],
        error: null,
        hasSwjInitialized: undefined,
        showDYBTutorial: undefined,
        showDYBTutorialCounter: undefined,
        showDYBTracker: undefined,
        showDYBTutorialV2: undefined,
        showDYBTutorialV2Counter: undefined,
        setUpdatedTiers: (
            updatedTiers: UpdatedJackpotTier[] | ((prev: UpdatedJackpotTier[]) => UpdatedJackpotTier[])
        ) =>
            set(
                (state) => ({
                    siteWideJackpot: {
                        ...state.siteWideJackpot,
                        updatedTiers:
                            typeof updatedTiers === 'function'
                                ? updatedTiers(state.siteWideJackpot.updatedTiers)
                                : updatedTiers
                    }
                }),
                undefined,
                'siteWideJackpot/setUpdatedTiers'
            ),

        activeIndex: 0,
        setActiveIndex: (index: number | ((prev: number) => number)) =>
            set(
                (state) => ({
                    siteWideJackpot: {
                        ...state.siteWideJackpot,
                        activeIndex: typeof index === 'function' ? index(state.siteWideJackpot.activeIndex) : index
                    }
                }),
                undefined,
                'siteWideJackpot/setActiveIndex'
            ),

        // MDS-related actions
        setHasSwjInitialized: (value: boolean | undefined) =>
            set(
                (state) => ({
                    siteWideJackpot: {
                        ...state.siteWideJackpot,
                        hasSwjInitialized: value
                    }
                }),
                undefined,
                'siteWideJackpot/setHasSwjInitialized'
            ),

        setOptInStatusError: (multiplier: MultiplierValue) =>
            set(
                (state) => ({
                    siteWideJackpot: {
                        ...state.siteWideJackpot,
                        multiplier
                    }
                }),
                undefined,
                'siteWideJackpot/setOptInStatusError'
            ),

        setDYBUserPreferences: (preferences: {
            showDYBTutorial?: boolean;
            showDYBTutorialCounter?: number;
            showDYBTracker?: boolean;
            showDYBTutorialV2?: boolean;
            showDYBTutorialV2Counter?: number;
        }) =>
            set(
                (state) => ({
                    siteWideJackpot: {
                        ...state.siteWideJackpot,
                        showDYBTutorial:
                            preferences.showDYBTutorial !== undefined
                                ? preferences.showDYBTutorial
                                : state.siteWideJackpot.showDYBTutorial,
                        showDYBTutorialCounter:
                            preferences.showDYBTutorialCounter !== undefined
                                ? preferences.showDYBTutorialCounter
                                : state.siteWideJackpot.showDYBTutorialCounter,
                        showDYBTracker:
                            preferences.showDYBTracker !== undefined
                                ? preferences.showDYBTracker
                                : state.siteWideJackpot.showDYBTracker,
                        showDYBTutorialV2:
                            preferences.showDYBTutorialV2 !== undefined
                                ? preferences.showDYBTutorialV2
                                : state.siteWideJackpot.showDYBTutorialV2,
                        showDYBTutorialV2Counter:
                            preferences.showDYBTutorialV2Counter !== undefined
                                ? preferences.showDYBTutorialV2Counter
                                : state.siteWideJackpot.showDYBTutorialV2Counter
                    }
                }),
                undefined,
                'siteWideJackpot/setDYBUserPreferences'
            )
    }
});
